import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import PropTypes from "prop-types";
import { openPreviewInNewTab } from "~/utils/fileUtils";
import { Messages } from "primereact/messages";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import API_ROUTES from "~/api-routes";
import apiService from "~/services/service";
import z from "zod";
import { RadioButton } from "primereact/radiobutton";
import { Skeleton } from "primereact/skeleton";
import useMessages from "../actions/useMessages";

const PayslipAwaitingReviewMessage = ({
  messageInfo,
  formatDate,
  fetchMessages,
}) => {
  const { id } = useParams();
  const { processdata } = useSelector((state) => state.process);
  const { processmilestones } = useSelector((state) => state.milestones);
  const { role: userRole } = useSelector((state) => state.login);
  const [employeesData, setEmployeesData] = useState([]);
  const [matchedFiles, setMatchedFiles] = useState([]);
  const [unmatchedFiles, setUnmatchedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [canEditMapping, setCanEditMapping] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const { submitPayslipMatchingResult, addPayslipSubmittedMessage } =
    useMessages({
      fetchMessages,
      relatedType: "PROCESS",
      serviceData: {},
      requestType: "",
    });

  const msgs = useRef(null);
  const prevWarningState = useRef(null);

  const actionOptions = [
    { name: "Approve", code: "approved", className: "approve-option" },
    { name: "Reject", code: "rejected", className: "reject-option" },
  ];

  const fetchMatchingResult = async (showLoader = true) => {
    if (showLoader) setIsLoading(true);
    try {
      const { data } = await apiService(API_ROUTES.PAYSLIP_MATCHING, "GET", {
        replace: { id, status: "" },
      });
      if (data?.length) {
        const filteredData = data.filter(
          (item) =>
            item.payslipMessageData?.id ===
              messageInfo?.payslipMessageData?.id &&
            item.match_status !== "source",
        );
        const matched = filteredData
          .filter(
            (payslip) =>
              payslip.match_status === "matched" ||
              payslip.match_status === "multiple_matches" ||
              payslip.match_status === "manually_corrected" ||
              payslip.match_status === "approved" ||
              payslip.match_status === "rejected" ||
              payslip.match_status === "released",
          )
          .map((file) => ({
            ...file,
            extracted_data: file.extracted_data || {},
          }));
        const unmatched = filteredData
          .filter(
            (payslip) =>
              payslip.match_status === "unmatched" ||
              payslip.match_status === "no_match" ||
              payslip.match_status === "invalidated" ||
              payslip.match_status === "extraction_failed",
          )
          .map((file) => {
            const existing = unmatchedFiles?.find((f) => f.id === file.id);
            return {
              ...file,
              extracted_data: {
                ...file.extracted_data,
                employee_id:
                  existing?.extracted_data?.employee_id ??
                  file.extracted_data?.employee_id ??
                  null,
              },
            };
          });
        setMatchedFiles(matched);
        setUnmatchedFiles(unmatched);
      } else {
        setMatchedFiles([]);
        setUnmatchedFiles([]);
      }
    } catch (error) {
      console.error("Error fetching matching results:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const { data } = await apiService(API_ROUTES.FETCH_EMPLOYEES, "GET", {
        replace: {
          id: processdata?.entity,
          page: 1,
          pageSize: 100,
          search: "",
          sortBy: "name",
          sortOrder: "ASC",
        },
        schema: z.object({
          id: z.string().uuid(),
          page: z.number().optional(),
          pageSize: z.number().optional(),
          search: z.string().optional(),
          sortBy: z
            .enum([
              "employee_identifier",
              "name",
              "work_email",
              "job_title",
              "employment_status",
              "start_date",
              "end_date",
            ])
            .optional(),
          sortOrder: z.enum(["ASC", "DESC"]).optional(),
        }),
      });
      const employeeList = data?.records || [];

      if (employeeList.length) {
        setEmployeesData(employeeList);
      } else {
        setEmployeesData([]);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const shouldShowWarning = unmatchedFiles.some(
    (file) => !file?.extracted_data?.employee_id,
  );

  useEffect(() => {
    if (!msgs.current) return;

    if (prevWarningState.current !== shouldShowWarning) {
      msgs.current.clear();
      if (shouldShowWarning) {
        msgs.current.show({
          severity: "warn",
          sticky: true,
          closable: true,
          detail: (
            <div>
              One or more files couldn’t be matched to an employee. This may be
              due to a mismatch with employee names or missing records in the
              registry.
              <Link
                className="request__link ml-2"
                to={`/entity/${processdata?.entity}/employee`}
              >
                View Employee Registry
              </Link>
            </div>
          ),
        });
      }
      prevWarningState.current = shouldShowWarning;
    }
  }, [shouldShowWarning, processdata?.entity]);

  useEffect(() => {
    fetchMatchingResult();
    if (processdata?.entity) fetchEmployees();
  }, [processdata]);

  useEffect(() => {
    if (processmilestones?.length) {
      const { taskData } = processmilestones?.find(
        (milestone) => milestone.name === "Submit Final Output",
      );
      const owner = taskData?.find(
        (task) => task.name === "Submit output to Customer",
      )?.owner;
      if (
        owner &&
        (["GXM_ADMIN"].includes(userRole) ||
          (owner === "LSP" && ["LSP_USER", "LSP_MANAGER"].includes(userRole)) ||
          (owner === "GXM" && ["GXM_USER", "GXM_MANAGER"].includes(userRole)))
      ) {
        setCanEditMapping(true);
      } else {
        setCanEditMapping(false);
      }
    }
  }, [processmilestones, userRole]);

  const employeeOptionsTemplate = (empId, employee) => {
    if (!employee) return null;
    return (
      <div className="flex align-items-center gap-2">
        <RadioButton
          checked={empId === employee.employee_identifier}
          onChange={() => {}}
        />
        <span>
          {employee.preferred_name || `${employee.name} ${employee.last_name}`}
        </span>
      </div>
    );
  };

  const employeeValueTemplate = (employee, props) => {
    if (!employee) {
      return <span>{props.placeholder}</span>;
    }
    return (
      <span>
        {employee.preferred_name || `${employee.name} ${employee.last_name}`}
      </span>
    );
  };

  const updateMatchedEmployee = (emp, options) => {
    const updated = [...matchedFiles];
    updated[options.rowIndex] = {
      ...updated[options.rowIndex],
      extracted_data: {
        ...updated[options.rowIndex].extracted_data,
        employee_id: emp.value?.employee_identifier,
        employee_name:
          emp.value.preferred_name ||
          `${emp.value.name} ${emp.value.last_name}`,
      },
    };
    setMatchedFiles(updated);
    disabledSubmitHanlder(updated);
  };

  const handleApproveAll = () => {
    const updated = matchedFiles.map((file) => ({
      ...file,
      match_status: "approved", // use the code, not name
    }));
    setMatchedFiles(updated);
    disabledSubmitHanlder(updated);
  };

  const disabledSubmitHanlder = (updatedMatchedFiles) => {
    const result = !updatedMatchedFiles.every((file) => {
      // Check if match_status is approved or rejected
      const hasValidStatus =
        file.match_status === "approved" || file.match_status === "rejected";

      // Check if employee_id exists in extracted_data
      const employeeId = file.extracted_data?.employee_id;

      // Check if this employee_id exists in employeesData
      const isEmployeeMatched =
        employeeId &&
        employeesData.some(
          (employee) => employee?.employee_identifier === employeeId,
        );

      return hasValidStatus && isEmployeeMatched;
    });

    setIsSubmitDisabled(result);
  };

  const updateUnmatchedEmployee = (emp, options) => {
    const updatedUnmatched = [...unmatchedFiles];
    const file = updatedUnmatched[options.rowIndex];

    const updatedFile = {
      ...file,
      extracted_data: {
        ...file.extracted_data,
        employee_id: emp.value?.employee_identifier,
      },
      match_status: "matched",
    };
    updatedUnmatched.splice(options.rowIndex, 1);

    setUnmatchedFiles(updatedUnmatched);
    setMatchedFiles((prev) => [...prev, updatedFile]);
    disabledSubmitHanlder([...matchedFiles, updatedFile]);
  };

  const submitHandler = async () => {
    try {
      setIsSubmitLoading(true);
      unmatchedFiles.map((fileItem) => (fileItem.match_status = "rejected"));
      const result = await Promise.all([
        submitPayslipMatchingResult(matchedFiles),
        ...(unmatchedFiles.length > 0
          ? [submitPayslipMatchingResult(unmatchedFiles)]
          : []),
      ]);
      if (result) {
        await fetchMatchingResult(false);
        const attachments = matchedFiles.map((fileItem) => ({
          fileName: getFilenameFromReference(fileItem?.file_reference),
          url: fileItem?.file_reference,
        }));
        await addPayslipSubmittedMessage(attachments);
      }
    } catch (error) {
      console.error("Error submitting matching results:", error);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const getEmployeePlaceHolder = (props) => {
    if (
      messageInfo?.payslipMessageData?.isSubmitted &&
      !props?.extracted_data?.employee_id
    ) {
      return "Unmatched";
    } else return "Select Employee";
  };

  const getFilenameFromReference = (fileReference) => {
    if (!fileReference) return "";
    try {
      const url = new URL(fileReference);
      const pathname = url.pathname;
      const filename = pathname.substring(pathname.lastIndexOf("/") + 1);
      return decodeURIComponent(filename);
    } catch (error) {
      console.error("Error extracting filename from reference:", error);
      return "";
    }
  };

  const getActionValue = (field) => {
    if (field?.match_status === "released") {
      actionOptions.push({
        name: "Released",
        code: "released",
        className: "released-option",
      });
    }
    return (
      actionOptions?.find((opt) => opt.code === field?.match_status) || null
    );
  };

  return (
    <div className="message-container">
      <Avatar
        icon={"pi pi-exclamation-circle"}
        shape="square"
        className="payslip-processing-message"
      />
      <div className="message-content">
        <div className="meta-info">
          <p className="user-name">
            {" "}
            The file you uploaded has been processed{" "}
          </p>
          <p className="date-time">{formatDate(messageInfo?.createdAt)}</p>
        </div>
        {messageInfo?.payslipMessageData?.content && (
          <p className="message">{messageInfo?.payslipMessageData?.content}</p>
        )}

        <div className="employee-info">
          {isLoading && (
            <div className="w-full inline-flex gap-2 mt-2">
              <Skeleton width="30%" className="mb-2"></Skeleton>
              <Skeleton width="30%" className="mb-2"></Skeleton>
              <Skeleton width="30%" className="mb-2"></Skeleton>
            </div>
          )}
          {matchedFiles.length > 0 && (
            <div className="matched-files-table mt-3">
              {!messageInfo?.payslipMessageData?.isSubmitted && (
                <div className="w-full inline-flex justify-content-between align-items-center mb-2">
                  <p className="matched-files-header">Matched Files</p>
                  <div className="table-action-buttons inline-flex gap-3">
                    <Button
                      className="cancel-btn approve-btn"
                      onClick={handleApproveAll}
                      disabled={!canEditMapping}
                    >
                      Approve All
                    </Button>
                    <Button
                      className="access-btn submit-matched-btn"
                      disabled={isSubmitDisabled}
                      onClick={submitHandler}
                      loading={isSubmitLoading}
                    >
                      Submit Matched Files
                    </Button>
                  </div>
                </div>
              )}

              <DataTable
                value={matchedFiles}
                className="datatable-right-align"
                emptyMessage="No matched employees found"
              >
                <Column
                  header="File"
                  field="attachment"
                  body={(field) => (
                    <span
                      className="file-name"
                      onClick={() =>
                        openPreviewInNewTab({
                          objectURL: field?.file_reference,
                        })
                      }
                    >
                      {getFilenameFromReference(field?.file_reference)}
                    </span>
                  )}
                  sortable
                  className="table-col-width"
                />
                <Column
                  header="Employee"
                  field="employeeName"
                  body={(field, options) => (
                    <div className="column__container__status border__none">
                      <Dropdown
                        value={
                          employeesData && field?.extracted_data?.employee_id
                            ? employeesData.find(
                                (emp) =>
                                  emp?.employee_identifier ===
                                  field?.extracted_data?.employee_id,
                              )
                            : null
                        }
                        onChange={(e) => updateMatchedEmployee(e, options)}
                        loading={!employeesData}
                        disabled={
                          !(
                            canEditMapping &&
                            !messageInfo?.payslipMessageData?.isSubmitted
                          )
                        }
                        options={employeesData || []}
                        optionLabel="name"
                        placeholder={getEmployeePlaceHolder(field)}
                        className="employee-dropdown employee-column-bg w-full justify-content-space-between"
                        filter={!!employeesData}
                        filterBy="name,last_name,preferred_name"
                        itemTemplate={(e) =>
                          employeeOptionsTemplate(
                            field?.extracted_data?.employee_id,
                            e,
                          )
                        }
                        valueTemplate={employeeValueTemplate}
                      />
                    </div>
                  )}
                  className={
                    canEditMapping
                      ? "employee-column-bg"
                      : "disabled-employee-column-bg"
                  }
                />
                <Column
                  header="Actions"
                  field="action"
                  body={(field, options) => (
                    <div className="column__container__status border__none">
                      <span
                        className={`action-badge ${field?.match_status && field?.match_status}`}
                      ></span>
                      <Dropdown
                        value={getActionValue(field)}
                        onChange={(e) => {
                          const updated = [...matchedFiles];
                          updated[options.rowIndex] = {
                            ...updated[options.rowIndex],
                            match_status: e.value.code,
                          };
                          setMatchedFiles(updated);
                          disabledSubmitHanlder(updated);
                        }}
                        loading={false}
                        disabled={
                          !(
                            canEditMapping &&
                            !messageInfo?.payslipMessageData?.isSubmitted
                          )
                        }
                        options={actionOptions}
                        optionLabel="name"
                        placeholder={
                          canEditMapping ? "Select Action" : "Pending Action"
                        }
                        className="action-dropdown w-full justify-content-space-between"
                      />
                    </div>
                  )}
                  className="table-col-width"
                />
              </DataTable>
            </div>
          )}
          {unmatchedFiles.length > 0 &&
            !messageInfo?.payslipMessageData?.isSubmitted && (
              <div className="unmatched-files-table mt-3">
                <p className="unmatched-files-header mb-4 mt-5">
                  Unmatched Files
                </p>
                <Messages className="mb-3" ref={msgs} />

                <DataTable
                  value={unmatchedFiles}
                  className="datatable-right-align"
                  emptyMessage="No matched employees found"
                >
                  <Column
                    header="File"
                    field="attachment"
                    body={(field) => (
                      <span
                        className="file-name"
                        onClick={() =>
                          openPreviewInNewTab({
                            objectURL: field?.file_reference,
                          })
                        }
                      >
                        {getFilenameFromReference(field?.file_reference)}
                      </span>
                    )}
                    sortable
                    className="table-col-width"
                  />
                  <Column
                    header="Employee"
                    field="employeeName"
                    body={(field, options) => (
                      <div className="column__container__status border__none">
                        <Dropdown
                          value={
                            employeesData && field?.extracted_data?.employee_id
                              ? employeesData.find(
                                  (emp) =>
                                    emp.employee_identifier ===
                                    field?.extracted_data?.employee_id,
                                ) || null
                              : null
                          }
                          onChange={(e) => updateUnmatchedEmployee(e, options)}
                          loading={!employeesData}
                          disabled={!canEditMapping} //tbc
                          options={employeesData || []}
                          optionLabel="name"
                          placeholder="Unmatched"
                          className="employee-dropdown employee-column-bg w-full justify-content-space-between"
                          filter={!!employeesData}
                          filterBy="name,last_name,preferred_name"
                          itemTemplate={(e) =>
                            employeeOptionsTemplate(
                              field?.extracted_data?.employee_id,
                              e,
                            )
                          }
                          valueTemplate={employeeValueTemplate}
                        />
                      </div>
                    )}
                    className={
                      canEditMapping
                        ? "employee-column-bg"
                        : "disabled-employee-column-bg"
                    }
                  />
                  <Column
                    header="Actions"
                    field="action"
                    body={(field, options) => {
                      const hasEmployee =
                        !!unmatchedFiles[options.rowIndex]?.extracted_data
                          ?.employee_id;

                      return (
                        <div className="column__container__status border__none">
                          <span
                            className={`status__badge--dot`}
                            style={{
                              backgroundColor: "#9e9e9e",
                            }}
                          ></span>
                          <Dropdown
                            value={
                              actionOptions?.find(
                                (opt) => opt.code === field?.match_status,
                              ) || null
                            }
                            onChange={(e) => {
                              const updated = [...unmatchedFiles];
                              updated[options.rowIndex] = {
                                ...updated[options.rowIndex],
                                match_status: e.value.code,
                              };
                              setUnmatchedFiles(updated);
                            }}
                            loading={false}
                            disabled={true}
                            options={actionOptions}
                            optionLabel="name"
                            placeholder={
                              canEditMapping
                                ? "Select Action"
                                : "Pending Action"
                            }
                            className="action-dropdown w-full justify-content-space-between"
                          />
                        </div>
                      );
                    }}
                    className="table-col-width"
                  />
                </DataTable>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

PayslipAwaitingReviewMessage.proptypes = {
  messageInfo: PropTypes.any,
  formatDate: PropTypes.func,
  fetchMessages: PropTypes.func,
};

export default PayslipAwaitingReviewMessage;
